import { Suspense } from 'react';
import Skeleton from '@/components/Skeleton.jsx';
import IconV2 from '@/components/v2/IconV2.jsx';
import { FiAlertTriangle, FiCheckCircle, FiFlag, FiInfo, FiRotateCw, FiSearch, FiUser, FiZap } from '@/components/react-icons/main';
import getDateTimeAgo from '@/lib/getDateTimeAgo.js';
import { getAppDefectDefinition } from 'shared/schema/appDefectCatalog.js';
import { Markdown, notesMarkdownComponents, summaryMarkdownComponents } from './constants.jsx';

// Each fact in a defect's history reads as one message from whoever caused it.
const conversationKindStyles = {
  detector: { icon: FiSearch, bg: '#f3f4f6', fg: '#6b7280' },
  person: { icon: FiUser, bg: '#eff6ff', fg: '#1d4ed8' },
  ai: { icon: FiZap, bg: '#f5f3ff', fg: '#7c3aed' },
  reviewer: { icon: FiRotateCw, bg: '#fffbeb', fg: '#b45309' },
  approved: { icon: FiCheckCircle, bg: '#dcfce7', fg: '#15803d' },
  blocked: { icon: FiFlag, bg: '#fef2f2', fg: '#b91c1c' },
  info: { icon: FiInfo, bg: '#f3f4f6', fg: '#6b7280' },
  error: { icon: FiAlertTriangle, bg: '#fef2f2', fg: '#b91c1c' }
};

// Turn a defect and its proposal history into an ordered conversation: who said
// what and when. Only human-facing text, no ids or machine records. The opening
// message comes from the defect (the reporter's words, or the detector's plain
// summary); every later message comes from a proposal (the agent's summary and
// the reviewer's comment) or a terminal job outcome that never produced one.
export const buildDefectConversation = (defect, proposals) => {
  const events = [];
  const fixJob = defect?.fixJob || {};
  const evidence = defect?.sampleEvidence || {};
  const openedAt = defect?.firstObservedOn || defect?.createdOn || defect?.lastObservedOn || null;
  const proposalList = Array.isArray(proposals) ? proposals : [];

  if (defect?.source === 'admin') {
    const reportText = typeof evidence.reportText === 'string' ? evidence.reportText.trim() : '';
    events.push({ at: openedAt, kind: 'person', author: 'Reported by a teammate', text: reportText || 'Reported this defect.' });
  } else {
    events.push({ at: openedAt, kind: 'detector', author: 'Detector', text: getAppDefectDefinition(defect?.issueCode)?.summary || 'An issue was detected on this app.' });
  }

  proposalList
    .slice()
    .sort((a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime())
    .forEach((proposal) => {
      if (proposal.summary) {
        events.push({ at: proposal.createdOn, kind: 'ai', author: 'App Fixer', text: proposal.summary, markdown: true });
      }
      if (proposal.reviewComment) {
        events.push({ at: proposal.reviewedOn || proposal.updatedOn, kind: 'reviewer', author: 'Reviewer', text: proposal.reviewComment });
      }
      if (proposal.status === 'approved') {
        events.push({ at: proposal.reviewedOn || proposal.updatedOn, kind: 'approved', author: 'Reviewer', text: proposal.appliedVersion ? `Approved. The fix is live as version ${proposal.appliedVersion}.` : 'Approved and published.' });
      }
    });

  // Retry prompts a reviewer typed when re-running a blocked defect. They read
  // like the request-changes comments above.
  (Array.isArray(fixJob.userPrompts) ? fixJob.userPrompts : []).forEach((prompt) => {
    if (prompt?.text) {
      events.push({ at: prompt.at, kind: 'reviewer', author: 'Reviewer', text: prompt.text });
    }
  });

  // Terminal outcomes that never produced a proposal. Gated on the current
  // status so a stale value left on fix_job by an earlier job never shows.
  if (defect?.status === 'blocked' && fixJob.blockReason) {
    events.push({ at: fixJob.finishedOn, kind: 'blocked', author: 'App Fixer', text: `Could not fix this automatically. ${fixJob.blockReason}`, markdown: true });
  }
  if (defect?.status === 'dismissed' && proposalList.length === 0 && fixJob.noFixReason) {
    events.push({ at: fixJob.finishedOn, kind: 'info', author: 'App Fixer', text: `Found nothing to change. ${fixJob.noFixReason}`, markdown: true });
  }
  if (defect?.status === 'open' && fixJob.lastError) {
    events.push({ at: fixJob.finishedOn, kind: 'error', author: 'System', text: `The fix run did not finish. ${fixJob.lastError}` });
  }

  return events
    .map((event, index) => ({ ...event, index }))
    .sort((a, b) => {
      const at = a.at ? new Date(a.at).getTime() : Number.POSITIVE_INFINITY;
      const bt = b.at ? new Date(b.at).getTime() : Number.POSITIVE_INFINITY;
      return at === bt ? a.index - b.index : at - bt;
    });
};

const DefectConversation = ({ events }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
    {events.map((event) => {
      const kind = conversationKindStyles[event.kind] || conversationKindStyles.info;
      return (
        <div key={event.index} style={{ display: 'flex', gap: 10 }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: kind.bg, color: kind.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <IconV2 icon={kind.icon} size={14} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{event.author}</span>
              {event.at && <span style={{ fontSize: 11, color: '#9ca3af' }}>{getDateTimeAgo(event.at)}</span>}
            </div>
            <div style={{ marginTop: 3, fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
              {event.markdown ? (
                <Suspense fallback={<span style={{ whiteSpace: 'pre-wrap' }}>{event.text}</span>}>
                  <Markdown components={event.kind === 'ai' ? summaryMarkdownComponents : notesMarkdownComponents}>{event.text}</Markdown>
                </Suspense>
              ) : (
                <span style={{ whiteSpace: 'pre-wrap' }}>{event.text}</span>
              )}
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

export default DefectConversation;

// One shimmer bar with the Skeleton primitive's padding and stacking margin
// neutralized so it honors the exact size passed in.
const SkeletonBar = ({ width, height, radius = 4, circle = false }) => <Skeleton count={1} circle={circle} containerStyle={{ padding: 0, width, flexShrink: 0 }} skeletonStyle={{ width: circle ? width : '100%', height, borderRadius: circle ? undefined : radius }} />;

// Mirrors one conversation message while the proposal history loads: the 28px
// avatar circle, the author and time line, then two message lines. The opening
// message renders instantly from the defect, so this stands in only for the
// agent and reviewer messages still coming from the server.
const DefectConversationSkeleton = () => (
  <div style={{ display: 'flex', gap: 10 }}>
    <SkeletonBar width={28} height={28} circle />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <SkeletonBar width={92} height={13} />
        <SkeletonBar width={58} height={11} />
      </div>
      <div style={{ marginTop: 7, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <SkeletonBar width="100%" height={12} />
        <SkeletonBar width="82%" height={12} />
      </div>
    </div>
  </div>
);

export { DefectConversationSkeleton };
